<template>
  <div class="mx-auto max-w-5xl p-4 text-sm mb-3" >
     <p>Tento dotazník cieli na dve príbuzné oblasti úzko previazané s motiváciou - dispozície kritického myslenia a sebaúčinnosť. Rozkladá ich do konkrétnych častí, ktoré zachytávajú, ako ľudia bežne premýšľajú, učia sa a riešia problémy v každodenných i záťažových situáciách.</p> 
     <p>Sú to <b>Critical Inquiry (dispozície kritického skúmania)</b> vyjadrujúca tendenciu integrovať informácie do širšieho konceptu, pracovať s viacerými zdrojmi, zvažovať perspektívy iných, zdôvodňovať vlastné závery, overovať dôveryhodnosť zdrojov a premýšľať o dôsledkoch rozhodnutí. 
      <b>Openness (otvorenosť)</b> zachytáva pripravenosť prijímať nové nápady, spochybňovať vlastné presvedčenia a adaptovať postupy, ak sa objavia lepšie riešenia. 
      <b>Reflective Practice (reflektívna prax)</b> meria návyk spätne hodnotiť skúsenosti, učiť sa z nich a systematicky zdokonaľovať vlastné konanie. 
      <b>SelfEfficacy (sebaúčinnosť)</b> označuje presvedčenie, že človek zvládne náročné situácie vďaka vytrvalosti, vynaliezavosti a schopnosti prispôsobiť sa nečakaným okolnostiam.
    </p>
    <p>Pri každom výroku určte mieru, v akej Vás výrok vystihuje v situáciách zvládania a riešenia náročných úloh. Použite päťstupnicové hodnotenie (1 = vôbec nie, 5 = úplne), pričom odpovedajte podľa toho, ako sa obvykle cítite a premýšľate, nie podľa ideálu.</p>
    <div class="text-left mb-3">
      <label class="inline-flex items-start gap-2">
        <input type="checkbox" v-model="agreement" :true-value="1" :false-value="0" />
        <span class="text-xs opacity-80">
          Súhlasím s anonymným spracovaním mojich odpovedí na vedecké účely.
        </span>
      </label>
    </div>

    <!-- RUNNING -->
    <div v-if="phase==='running'">
      <div class="table-wrapper">
        <table class="likert-table">
          <thead>
            <tr>
              <th class="th-num">Otázka</th>
              <th v-for="opt in likertRev" :key="'h-'+opt.value" class="col-opt">
                <div class="th-num">{{ opt.value }}</div>
                <div class="th-sub">{{ opt.head }}</div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="q in questions" :key="'q-'+q.id">
              <td class="qtext">
                <span class="qid">{{ q.id }}.</span> {{ q.text }}
              </td>
              <td v-for="opt in likertRev" :key="'c-'+q.id+'-'+opt.value" class="opt-cell">
                <input type="radio"
                       :name="'q-'+q.id"
                       v-model.number="responses[q.id]"
                       :value="opt.value"
                       :aria-label="'Otázka '+q.id+', voľba '+opt.value" 
                       :disabled="isLocked" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="flex items-center justify-between mt-4 btn-bar">
        <div class="text-xs opacity-80">
          Zodpovedané: {{ answeredCount }} / {{ questions.length }} 
        </div>
        <v-btn class="visible-disabled" :disabled="answeredCount<questions.length" color="primary" @click="submit" style="margin-top: -10px;">
          Odoslať
        </v-btn>
      </div>
    </div>

    <!-- FINISHED -->
    <div v-else-if="phase==='finished'" class="mt-4">
      <v-card outlined class="pa-3 mt-4">     
        <!-- Radar -->
        <div class="max-w-xl">
          <canvas ref="radarCanvas" aria-label="Radar chalenging" role="img"></canvas>
        </div>

        <div class="d-flex justify-end">
          <v-btn color="primary" type="submit" class="mr-2" @click="closeTest">Koniec</v-btn>
        </div>
      </v-card>

    </div>
  </div>
</template>

<script>
import axios from '../../axios-auth.js'
import {
  Chart, RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend
} from 'chart.js';
Chart.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

export default {
  name: "MetaCognitionLikert5Table",
  props: {
    beaconUrl: { type: String, default: "" },
    participantId: { type: [String, Number], default: "" }
  },
  data() {
    return {
      phase: 'ready',
      startTime: null,
      elapsedSec: 0,
      secTimer: null,
      finalized: false,
      radar: null,
      agreement : 0,

      // Likert: zobrazíme v poradí 5 -> 1
      likert: [
        { value: 1, head: 'vôbec nie' },
        { value: 2, head: 'skôr nie' },
        { value: 3, head: 'neutrálne' },
        { value: 4, head: 'skôr áno' },
        { value: 5, head: 'úplne' },
      ],

questions: [
  { id: 1,  text: 'Zvyčajne sa pri diskusii snažím myslieť na celý koncept problému.', construct: 'CI' },
  { id: 2,  text: 'Často používam nové nápady na to, aby som zmenil spôsob, akým robím veci.', construct: 'OP' },
  { id: 3,  text: 'Používam viac než len jeden zdroj na hľadanie informácií.', construct: 'CI' },
  { id: 4,  text: 'Často hľadám nové nápady.', construct: 'OP' },
  { id: 5,  text: 'Niekedy nájdem dobrý argument, ktorý spochybňuje niektoré z mojich pevných presvedčení.', construct: 'OP' },
  { id: 6,  text: 'Je dôležité porozumieť aj pohľadu iných ľudí na problém.', construct: 'CI' },
  { id: 7,  text: 'Je dôležité vedieť zdôvodniť svoje rozhodnutia.', construct: 'CI' },
  { id: 8,  text: 'Často prehodnocujem svoje skúsenosti, aby som sa z nich mohol poučiť.', construct: 'RP' },
  { id: 9,  text: 'Predtým, ako spravím rozhodnutie, zvyčajne kontrolujem dôveryhodnosť zdroja informácií.', construct: 'CI' },
  { id: 10, text: 'Zvyčajne premýšľam o širších dôsledkoch svojho rozhodnutia predtým, ako podniknem nejaké kroky.', construct: 'CI' },
  { id: 11, text: 'Často premýšľam nad svojím konaním, aby som zistil, či ho môžem vylepšiť.', construct: 'RP' },
  { id: 12, text: 'Teraz, prosím, ak ste si stále vedomí prebiehajúceho testovania, označ v riadku druhú položku zľava.', construct: 'AttentionCheck' },
  { id: 13, text: 'Vždy si viem poradiť s riešením náročných problémov, keď sa o to dostatočne snažím.', construct: 'SE' },
  { id: 14, text: 'Ak je niekto voči mne v opozícii, dokážem nájsť prostriedky a spôsoby, ako dosiahnuť to, čo chcem.', construct: 'SE' },
  { id: 15, text: 'Je pre mňa ľahké držať sa toho, čo som si zaumienil/la a dosiahnuť to.', construct: 'SE' },
  { id: 16, text: 'Vďaka svojej dôvtipnosti sa dokážem vysporiadať s nepredvídateľnými situáciami.', construct: 'SE' },
  { id: 17, text: 'Som si istý/á, že dokážem účinne zvládnuť neočakávané udalosti.', construct: 'SE' },
  { id: 18, text: 'Viem nájsť východisko temer z každého problému, ak vynaložím patričné úsilie.', construct: 'SE' },
  { id: 19, text: 'Dokážem zostať chladným/nou zoči-voči ťažkostiam, lebo sa môžem spoliehať na svoje schopnosti vynájsť sa.', construct: 'SE' },
  { id: 20, text: 'Keď stojím pred nejakým problémom, zvykne ma napadnúť viac spôsobov ako na to.', construct: 'SE' },
  { id: 21, text: 'Ak som „v kaši“, obvykle vymyslím, ako sa z nej dostať.', construct: 'SE' },
  { id: 22, text: 'Bez ohľadu na to, čo sa stane, som obvykle v stave sa s tým vysporiadať.', construct: 'SE' },
],


      responses: {}, // { [id:number]: 1..5 }
      scales: {
        CI: null,
        OP: null,
        RP: null,          // bolo RO
        SE: null,
//        Global: null,
//        AttentionCheck: null
      }
    };
  },
  computed: {
    answeredCount() {
      return Object.keys(this.responses).length;
    },
    allIds() {
      return this.questions.map(function(q){ return String(q.id); });
    },
    likertRev() {
      // 5 -> 1
      return this.likert.slice().sort(function(a,b){ return b.value - a.value; });
    },
    isLocked() { return this.agreement !== 1; },
  },
  mounted() {
    this.startTime = Date.now()
    this.start()
  },
  methods: {
    // helpers
    valOr(v, fb) { return (v!=null ? v : fb); },
    toFixedSafe(x, d) { return (typeof x==='number' && isFinite(x)) ? x.toFixed(d) : ''; },
    fmt(v) { return (v==null || isNaN(v)) ? '—' : Number(v).toFixed(2); },

    start() {
      this.phase = 'running';
      this.responses = {};     
      this.elapsedSec = 0;
      if (this.secTimer) clearInterval(this.secTimer);
      this.secTimer = setInterval(() => { this.elapsedSec++; }, 1000);
      window.addEventListener('beforeunload', this.onBeforeUnload, true);
      window.addEventListener('pagehide', this.onPageHide, true);
    },
    restart() {
      this.cleanup();
      this.destroyRadar();
      this.phase = 'ready';
    },
    meanByKeys(keys) {
      const vals = keys.map(k => this.responses[String(k)]).filter(v => typeof v === 'number');
      if (!vals.length) return null;
      let s = 0; for (let i=0;i<vals.length;i++) s += vals[i];
      return s / vals.length;
    },
    computeScales() {
      // mapovanie položiek → konštrukty
      const CI = ['1','3','6','7','9','10'];                          // CriticalInquiry
      const OP = ['2','4','5'];                                       // Openness
      const RP = ['8','11'];                                          // ReflectivePractice
      const SE = ['13','14','15','16','17','18','19','20','21','22']; // SelfEfficacy
      const AC = ['12'];                                              // AttentionCheck (nekumuluje sa)

      const SCORABLE = [...CI, ...OP, ...RP, ...SE];                  // bez AC

      this.scales.CI = this.meanByKeys(CI);
      this.scales.OP = this.meanByKeys(OP);
      this.scales.RP = this.meanByKeys(RP);
      this.scales.SE = this.meanByKeys(SE);
//      this.scales.Global = this.meanByKeys(SCORABLE);

      // pre QC môžeš mať k dispozícii aj IMC hodnotu:
//      this.scales.AttentionCheck = this.meanByKeys(AC);
    },
    submit() {
      if (this.answeredCount < this.questions.length) return;
      if (this.secTimer) { clearInterval(this.secTimer); this.secTimer = null; }
      this.computeScales();
      this.phase = 'finished';
      this.$nextTick(() => this.drawRadar());

      const summary = this.summarize('completed');
      this.emitAndBeacon(summary);
      this.finalized = true;
      window.removeEventListener('beforeunload', this.onBeforeUnload, true);
      window.removeEventListener('pagehide', this.onPageHide, true);
    },
    summarize(end_reason) {
      this.computeScales();
      this.writeAnswer(end_reason)
      return {
        participant_id: this.participantId || null,
        end_reason,
        duration_sec: this.startTime ? Math.round((performance.now() - this.startTime)/1000) : null,
        n_items: this.questions.length,
        n_answered: this.answeredCount,
        scales: { ...this.scales },
        responses: { ...this.responses },
        client_time_iso: new Date().toISOString()
      };
    },/*
    drawRadar() {
      const ctx = this.$refs.radarCanvas;
      if (!ctx) return;
      this.destroyRadar();

      const labels = ['Dispozície kritického skúmania', 'Otvorenosť', 'Reflexívna prax', 'Sebaúčinnosť'];
      const data = [
        this.valOr(this.scales.CI,   0),
        this.valOr(this.scales.OP,  0),
        this.valOr(this.scales.RP, 0),
        this.valOr(this.scales.SE, 0)
//        this.valOr(this.scales.Global,     0)
      ];

      this.radar = new Chart(ctx, {
        type: 'radar',
        data: { labels, datasets: [{ label: 'Chalenging (1-5)', data, fill: true }] },
        options: {
          responsive: true,
          plugins: { legend: { display: true }, tooltip: { enabled: true } },
          scales: {
            r: { suggestedMin: 1, suggestedMax: 5, ticks: { stepSize: 1 }, pointLabels: { font: { size: 12 } } }
          }
        }
      });
    },*/
    drawRadar() {
      const ctx = this.$refs.radarCanvas;
      if (!ctx) return;
      this.destroyRadar();

      const C = this.radarColorsUniversal();

      const labels = ['Dispozície kritického skúmania', 'Otvorenosť', 'Reflexívna prax', 'Sebaúčinnosť'];
      const data = [
        this.valOr(this.scales.CI, 0),
        this.valOr(this.scales.OP, 0),
        this.valOr(this.scales.RP, 0),
        this.valOr(this.scales.SE, 0),
      ];

      this.radar = new Chart(ctx, {
        type: 'radar',
        data: {
          labels,
          datasets: [{
            label: 'Challenging (1–5)',
            data,
            borderColor: C.border,
            backgroundColor: C.fill,
            borderWidth: 2,
            pointRadius: 3,
            pointBackgroundColor: C.point,
            pointBorderColor: C.border,
            pointHoverBackgroundColor: C.point,
            pointHoverBorderColor: C.border,
          }]
        },
        options: {
          responsive: true,
          plugins: {
            legend: { labels: { color: C.text } },
            tooltip: { enabled: true }
          },
          scales: {
            r: {
              min: 1, max: 5,
              ticks: {
                stepSize: 1,
                color: C.text,
                backdropColor: 'rgba(0,0,0,0)' // žiadne sivé štvorčeky pod číslami
              },
              grid: { color: C.grid, lineWidth: 1 },
              angleLines: { color: C.angle, lineWidth: 1 },
              pointLabels: { color: C.text, font: { size: 12 } }
            }
          }
        }
      });
    },
    destroyRadar() { if (this.radar) { this.radar.destroy(); this.radar = null; } },
    emitAndBeacon(summary) {
      this.$emit('finalize', summary);
      try { localStorage.setItem('metacog_summary', JSON.stringify(summary)); } catch(e) {}
      if (this.beaconUrl) {
        try {
          const blob = new Blob([JSON.stringify(summary)], { type: 'application/json' });
          navigator.sendBeacon(this.beaconUrl, blob);
        } catch(e) {}
      }
    },
    onBeforeUnload() {
      if (this.phase === 'running' && !this.finalized) {
        const summary = this.summarize('abandon');
        this.emitAndBeacon(summary);
        this.finalized = true;
      }
    },
    onPageHide() {
      if (this.phase === 'running' && !this.finalized) {
        const summary = this.summarize('abandon');
        this.emitAndBeacon(summary);
        this.finalized = true;
      }
    },
    cleanup() {
      if (this.secTimer) clearInterval(this.secTimer);
      this.secTimer = null;
      window.removeEventListener('beforeunload', this.onBeforeUnload, true);
      window.removeEventListener('pagehide', this.onPageHide, true);
    },
    roundObj(obj, decimals = 3) {
      const k = Math.pow(10, decimals);
      return Object.fromEntries(
        Object.entries(obj || {}).map(([key, val]) => [
          key,
          (typeof val === 'number' && isFinite(val)) ? Math.round(val * k) / k : val
        ])
      );
    },
    getBaseTextColor() {
      const base = getComputedStyle(this.$el).color || '#222';
      return base; // môže byť "rgb(...)" alebo "rgba(...)" alebo "#hex"
    },
    toRGBA(color, alpha=1) {
      // prevedie rgb/rgba/#hex na rgba(r,g,b,a)
      const ctx = document.createElement('canvas').getContext('2d');
      ctx.fillStyle = color;            // nech prehliadač spraví parsing
      const parsed = ctx.fillStyle;     // vždy v podobe "rgb(r, g, b)"
      const m = parsed.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
      const [_, r, g, b] = m || [0, 34, 34, 34]; // fallback #222
      return `rgba(${r||34}, ${g||34}, ${b||34}, ${alpha})`;
    },
    radarColorsUniversal() {
      const text = this.getBaseTextColor();
      return {
        text,
        grid:  this.toRGBA(text, 0.22),
        angle: this.toRGBA(text, 0.30),
        // neutrálna, „bezbrandová“ akcentová farba (teal)
        border: '#14b8a6',                          // teal-500
        fill:   'rgba(20, 184, 166, 0.22)',         // priesvitná výplň
        point:  '#14b8a6'
      };
    },
    writeAnswer(_end_reason) {
      const _data = {
          id: 0,
          test_id: this.$route.params.id,
          score: -1 ,
          answers : JSON.stringify(this.responses), 
          final: _end_reason == 'completed' ? 1 : 0,
          rslt: JSON.stringify(this.roundObj(this.scales, 3)),
          agreement: this.agreement,
          time_length: this.startTime ? Math.round((Date.now() - this.startTime)/1000) : 0
        } 
        axios.post('/write-my-person-test-answer', _data, this.$store.getters.getAxiosConfig)
        .then(res => {
            // this.answer_id = res.data.answer_id
        })
        .catch(error => {
            this.$store.commit('setFeedback', error)
        })
    },
    closeTest() {
      this.$router.push({name:'personalisation_tests', params: {}}) 
    },
  },
  beforeUnmount() {
    this.cleanup();
    this.destroyRadar();
  }
};
</script>

<style scoped>
.max-w-5xl { max-width: 64rem; }
.max-w-xl  { max-width: 36rem; }
.p-4 { padding: 1rem; }
.mx-auto { margin-left: auto; margin-right: auto; }
.mb-2 { margin-bottom: .5rem; }
.mb-3 { margin-bottom: .75rem; }
.mt-4 { margin-top: 1rem; }
.text-xl { font-size: 1.25rem; line-height: 1.75rem; }
.font-semibold { font-weight: 600; }
.text-sm { font-size: .875rem; }
.text-xs { font-size: .75rem; }
.opacity-80 { opacity: .8; }
.cursor-pointer { cursor: pointer; }
.overflow-auto { overflow: auto; }

/* Tabuľka Likertu */
.table-wrapper { overflow-x: auto; }
.likert-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
.likert-table th, .likert-table td { border: 1px solid #e5e7eb; padding: .5rem; }
.col-question { width: 60%; text-align: left; background: #fafafa; }
.col-opt { width: 8%; text-align: center; }
.qtext { text-align: left; }
.qid { font-weight: 600; margin-right: .25rem; }
.opt-cell { text-align: center; vertical-align: middle; }
.opt-cell input[type="radio"] { width: 1.1rem; height: 1.1rem; cursor: pointer; }

.th-num { font-weight: 700; }
.th-sub { font-size: .7rem; opacity: .8; }

/* Radar kontajner */
.max-w-xl { max-width: 36rem; }

.btn-bar { position: relative; }
.btn-bar .visible-disabled {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
}
</style>
